/**
 * JsonlDiagSink — buffered, never-crashing JSONL appender for per-session voice diagnostics.
 * One JSON object per line; events queue in memory and drain via async fs.appendFile (no fsync
 * ceremony). Any write/serialize failure logs once at debug and disables the sink — a diagnostics
 * file must never crash or slow a live voice session. Consumers: the CLI voice sink
 * (`<session>.voice.jsonl`) and the duplex-lab timeline (`<session>.timeline.jsonl`).
 */
import { appendFile, mkdirSync } from 'node:fs';
import { dirname } from 'node:path';
import { forComponent } from '../logging';

const log = forComponent('VoiceDiag');

export class JsonlDiagSink {
  private queue: string[] = [];
  private writing = false;
  private disabled = false;
  private dirReady = false;

  constructor(public readonly path: string) {}

  /** Append one event as a JSON line (buffered; returns immediately). */
  push(ev: Record<string, unknown>): void {
    if (this.disabled) return;
    try { this.queue.push(JSON.stringify(ev)); } catch { return; } // unserializable event → drop it, keep the sink
    this.flush();
  }

  private flush(): void {
    if (this.writing || this.disabled || !this.queue.length) return;
    if (!this.dirReady) {
      try { mkdirSync(dirname(this.path), { recursive: true }); this.dirReady = true; }
      catch (e) { return this.disable(e); }
    }
    this.writing = true;
    const lines = this.queue.splice(0).join('\n') + '\n';
    appendFile(this.path, lines, (e) => {
      this.writing = false;
      if (e) return this.disable(e);
      this.flush(); // drain anything queued while the write was in flight
    });
  }

  private disable(e: unknown): void {
    this.disabled = true;
    this.queue.length = 0;
    log.debug(`voice diag sink disabled (${this.path}): ${e instanceof Error ? e.message : e}`);
  }

  /** True once a failure permanently disabled the sink (tests/inspection). */
  get isDisabled(): boolean { return this.disabled; }

  /** Resolve when all queued lines are on disk (or the sink disabled) — tests/graceful shutdown. */
  async settle(): Promise<void> {
    while (!this.disabled && (this.writing || this.queue.length)) await new Promise((r) => setTimeout(r, 5));
  }
}
